import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDecimal, formatFixed, type Decimal } from '@globalx/core'
import { usePreferences } from '@/store/preferences'

type Num = Decimal | string | number

/** Locale-aware number helpers bound to the user's language and numeral style. */
export function useFormat() {
  const { i18n } = useTranslation()
  const digits = usePreferences((s) => s.digits)
  const locale = i18n.language

  const fixed = useCallback(
    (value: Num, fractionDigits: number) => formatFixed(value, fractionDigits, { locale, digits }),
    [locale, digits],
  )

  const percent = useCallback(
    (value: number, fractionDigits = 2) =>
      formatDecimal(value, {
        locale,
        digits,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
        signDisplay: 'exceptZero',
      }) + '%',
    [locale, digits],
  )

  const currencyName = useMemo(() => {
    let names: Intl.DisplayNames | null = null
    try {
      names = new Intl.DisplayNames([locale], { type: 'currency' })
    } catch {
      names = null
    }
    return (code: string) => names?.of(code) ?? code
  }, [locale])

  const dateTime = useCallback(
    (iso: string) =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        numberingSystem: digits,
      }).format(new Date(iso)),
    [locale, digits],
  )

  const date = useCallback(
    (iso: string) =>
      new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC', numberingSystem: digits }).format(new Date(iso)),
    [locale, digits],
  )

  const relative = useCallback(
    (iso: string, now = Date.now()) => {
      const diff = (Date.parse(iso) - now) / 1000
      const abs = Math.abs(diff)
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', numberingSystem: digits } as Intl.RelativeTimeFormatOptions)
      if (abs < 60) return rtf.format(Math.round(diff), 'second')
      if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute')
      if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour')
      return rtf.format(Math.round(diff / 86400), 'day')
    },
    [locale, digits],
  )

  return { locale, digits, fixed, percent, currencyName, date, dateTime, relative }
}
